import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { SiteLogo } from "@/components/SiteLogo";
import { SITE_NAME, SITE_TAGLINE } from "@shared/site";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Link } from "wouter";
import { CheckCircle, ArrowLeft, Megaphone } from "lucide-react";

const BUDGET_OPTIONS = [
  "Under $500/month",
  "$500 – $1,000/month",
  "$1,000 – $2,500/month",
  "$2,500 – $5,000/month",
  "$5,000+/month",
  "Let's discuss",
];

export default function Advertise() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    budget: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const prev = document.title;
    document.title = `Advertise — ${SITE_NAME}`;
    return () => {
      document.title = prev;
    };
  }, []);

  const send = trpc.forms.advertise.useMutation({
    onSuccess: () => setSubmitted(true),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    send.mutate({
      name: form.name,
      company: form.company,
      email: form.email,
      phone: form.phone || undefined,
      budget: form.budget || undefined,
      message: form.message,
    });
  };

  const set = (field: keyof typeof form) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border/50 bg-background/95 backdrop-blur-md sticky top-0 z-40">
        <div className="container flex items-center gap-3 h-14">
          <Link href="/">
            <button className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors text-sm font-medium">
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </Link>
          <span className="text-border/50">|</span>
          <Link href="/" className="text-foreground hover:opacity-90 transition-opacity flex items-center gap-2">
            <SiteLogo className="h-8" />
            <span className="sr-only">
              {SITE_NAME} — {SITE_TAGLINE}
            </span>
          </Link>
        </div>
      </header>

      <main className="container py-12 max-w-xl mx-auto">
        {submitted ? (
          <div className="flex flex-col items-center gap-5 py-16 text-center">
            <CheckCircle className="w-14 h-14" style={{ color: "var(--pb-green)" }} />
            <h2
              className="text-3xl font-black tracking-tight"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              INQUIRY RECEIVED!
            </h2>
            <p className="text-muted-foreground">
              Thanks for your interest in advertising on {SITE_NAME}. We'll be in touch at{" "}
              <strong>{form.email}</strong> shortly.
            </p>
            <Link href="/">
              <Button
                className="mt-2 font-bold text-black"
                style={{ background: "var(--pb-green)" }}
              >
                Back to Radio
              </Button>
            </Link>
          </div>
        ) : (
          <>
            {/* Hero copy */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-2">
                <Megaphone className="w-5 h-5" style={{ color: "var(--pb-green)" }} />
                <span
                  className="text-xs font-bold tracking-widest uppercase"
                  style={{ color: "var(--pb-green)" }}
                >
                  Reach Pickleball Players
                </span>
              </div>
              <h1
                className="text-4xl font-black tracking-tight mb-2"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                ADVERTISE WITH US
              </h1>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {SITE_NAME} streams 24/7 across 7 genre stations to a dedicated community of pickleball
                players. Put your brand in front of the fastest-growing sport in the world.
              </p>

              {/* Quick stats */}
              <div className="grid grid-cols-3 gap-3 mt-5">
                {[
                  { value: "7", label: "Live Stations" },
                  { value: "24/7", label: "Non-Stop" },
                  { value: "100%", label: "Pickleball" },
                ].map(stat => (
                  <div
                    key={stat.label}
                    className="rounded-xl p-3 text-center"
                    style={{ background: "var(--pb-card)" }}
                  >
                    <div
                      className="text-2xl font-black"
                      style={{ color: "var(--pb-green)", fontFamily: "'Barlow Condensed', sans-serif" }}
                    >
                      {stat.value}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Your Name *
                  </Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Jane Smith"
                    required
                    className="bg-card border-border/60"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="company" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Company / Brand *
                  </Label>
                  <Input
                    id="company"
                    value={form.company}
                    onChange={set("company")}
                    placeholder="Acme Pickleball Co."
                    required
                    className="bg-card border-border/60"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="adv-email" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Email Address *
                  </Label>
                  <Input
                    id="adv-email"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="jane@acme.com"
                    required
                    className="bg-card border-border/60"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="phone" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Phone (optional)
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="+1 (555) 000-0000"
                    className="bg-card border-border/60"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="budget" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Monthly Budget (optional)
                </Label>
                <select
                  id="budget"
                  value={form.budget}
                  onChange={set("budget")}
                  className="flex h-10 w-full rounded-md border border-border/60 bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[var(--pb-green)] focus:ring-offset-0"
                >
                  <option value="">Select a range...</option>
                  {BUDGET_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="adv-message" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Tell us about your campaign *
                </Label>
                <Textarea
                  id="adv-message"
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Describe your product, target audience, and what you're hoping to achieve..."
                  required
                  rows={5}
                  className="bg-card border-border/60 resize-none"
                />
              </div>

              {send.error && (
                <p className="text-destructive text-sm">
                  {send.error.message || "Something went wrong. Please try again."}
                </p>
              )}

              <Button
                type="submit"
                disabled={send.isPending}
                className="w-full font-bold text-black text-base py-3 h-auto"
                style={{ background: "var(--pb-green)" }}
              >
                {send.isPending ? "Sending..." : "Submit Inquiry"}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                We typically respond within 1 business day.
              </p>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
